import Markdown from '@components/utils/mdText';
import { StyledProps } from '@emotion/react';
import styled from '@emotion/styled';
import { AnimatePresence, motion, Variants } from 'framer-motion';
import { FC, useCallback, useState } from 'react';

import { useFlowPage } from '../contexts/FlowPageContext';
import { Alignment } from '../types';
import { FlowPageLoader } from './FlowPageLoader';

const DescriptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: hidden;
    position: relative;
    width: 100%;
`;

const Description = styled.div<
    StyledProps<{ hasShadow: boolean; textAlign: Alignment }>
>`
    position: relative;
    overflow-y: auto;
    text-align: ${({ textAlign }) => textAlign};
    * {
        color: ${({ theme }) => theme.palette.common.white};
    }
`;

const OverflowShadow = styled(motion.div)`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
`;

const overflowShadowMotion: Variants = {
    initial: { boxShadow: 'none' },
    animate: { boxShadow: '0 0 12px 4px rgba(0, 0, 0, 0.2)' },
    exit: { boxShadow: 'none' },
};

export const FlowPageDescription: FC = () => {
    const { description, descriptionAlignment } = useFlowPage();
    const [hasOverflowShadow, setHasOverflowShadow] = useState(false);

    const isScrolledToBottom = useCallback((scrollableDiv: HTMLDivElement) => {
        const { scrollHeight, scrollTop, clientHeight } = scrollableDiv;
        return Math.floor(scrollHeight - scrollTop) <= clientHeight + 1;
    }, []);

    const handleOverflowShadow = useCallback(
        (scrollableDiv: HTMLDivElement) => {
            setHasOverflowShadow(!isScrolledToBottom(scrollableDiv));
        },
        [isScrolledToBottom]
    );

    const overflowShadowRef = useCallback(
        (scrollableDiv: HTMLDivElement | null) => {
            if (scrollableDiv !== null) {
                handleOverflowShadow(scrollableDiv);
            }
        },
        [handleOverflowShadow]
    );

    if (!description) return null;

    return (
        <DescriptionContainer>
            <Description
                textAlign={descriptionAlignment}
                ref={overflowShadowRef}
                onScroll={(e) => handleOverflowShadow(e.currentTarget)}
            >
                <Markdown textColor='white' markdown={description} />
            </Description>
            <FlowPageLoader />
            <AnimatePresence>
                {hasOverflowShadow && (
                    <OverflowShadow
                        variants={overflowShadowMotion}
                        initial='initial'
                        animate='animate'
                        exit='exit'
                    />
                )}
            </AnimatePresence>
        </DescriptionContainer>
    );
};
